import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { AlertTriangle, XCircle, Info, X, ChevronRight } from 'lucide-react';
import { GET_LICENCIA_SISTEMA } from '../graphql/licencia';
import './AlertaLicencia.css';

/**
 * Componente que muestra alertas de licencia en el header/layout
 *
 * Niveles de alerta:
 * - critico (rojo): Suspendida o vencida sin gracia - NO permite cerrar
 * - urgente (naranja): En gracia o vence en < 7 dias - NO permite cerrar
 * - advertencia (amarillo): Vence en 8-30 dias - SI permite cerrar (sessionStorage)
 */
function AlertaLicencia() {
  const navigate = useNavigate();
  const [cerrada, setCerrada] = useState(false);

  // Query GraphQL - solo para verificar estado
  const { data } = useQuery(GET_LICENCIA_SISTEMA, {
    fetchPolicy: 'cache-first',
    pollInterval: 300000 // Verificar cada 5 minutos
  });

  const licencia = data?.licenciaSistema;

  // Verificar si fue cerrada en esta sesion (solo para advertencias)
  useEffect(() => {
    const alertaCerrada = sessionStorage.getItem('alerta_licencia_cerrada');
    if (alertaCerrada) {
      setCerrada(true);
    }
  }, []);

  // Calcular alerta basada en el estado de la licencia
  const calcularAlerta = () => {
    if (!licencia) return null;

    // Si es licencia permanente (compra), no mostrar alerta
    if (licencia.permanente) return null;

    // Si esta suspendida
    if (licencia.estado_licencia === 'suspendida') {
      return {
        tipo: 'critico',
        icono: <XCircle size={20} />,
        mensaje: 'Licencia suspendida. Contacte al administrador para mas informacion.',
        permiteCerrar: false
      };
    }

    // Si no tiene licencia activada
    if (licencia.estado_licencia === 'sin_activar') {
      return null; // No mostrar alerta, la pagina lo manejara
    }

    // Si esta vencida
    if (licencia.estado_licencia === 'vencida') {
      return {
        tipo: 'critico',
        icono: <XCircle size={20} />,
        mensaje: 'Su licencia ha expirado. El sistema sera bloqueado.',
        permiteCerrar: false
      };
    }

    // Si esta en gracia
    if (licencia.en_gracia) {
      const diasEnGracia = licencia.dias_gracia + licencia.dias_restantes;
      return {
        tipo: 'urgente',
        icono: <AlertTriangle size={20} />,
        mensaje: `Su licencia ha vencido. Quedan ${diasEnGracia} dias de gracia para renovar.`,
        permiteCerrar: false
      };
    }

    // Calcular dias restantes
    const diasRestantes = licencia.dias_restantes;

    if (diasRestantes === null || diasRestantes === undefined) return null;

    if (diasRestantes <= 7) {
      return {
        tipo: 'urgente',
        icono: <AlertTriangle size={20} />,
        mensaje: `URGENTE: Su licencia vence en ${diasRestantes} dia${diasRestantes !== 1 ? 's' : ''}. Renueve ahora.`,
        permiteCerrar: false
      };
    }

    if (diasRestantes <= 30) {
      // Verificar si ya fue cerrada en esta sesion
      if (cerrada) return null;

      return {
        tipo: 'advertencia',
        icono: <Info size={20} />,
        mensaje: `Su licencia vence en ${diasRestantes} dias.`,
        permiteCerrar: true
      };
    }

    return null;
  };

  const alerta = calcularAlerta();

  const handleCerrar = () => {
    sessionStorage.setItem('alerta_licencia_cerrada', 'true');
    setCerrada(true);
  };

  const handleClick = () => {
    navigate('/mi-licencia');
  };

  if (!alerta) return null;

  return (
    <div className={`alerta-licencia alerta-licencia--${alerta.tipo}`}>
      <div className="alerta-licencia__icono">
        {alerta.icono}
      </div>
      <div className="alerta-licencia__contenido" onClick={handleClick}>
        <span className="alerta-licencia__mensaje">
          {alerta.mensaje}
        </span>
      </div>
      <button className="alerta-licencia__link" onClick={handleClick}>
        Ver detalles
        <ChevronRight size={16} />
      </button>
      {alerta.permiteCerrar && (
        <button className="alerta-licencia__cerrar" onClick={handleCerrar}>
          <X size={18} />
        </button>
      )}
    </div>
  );
}

export default AlertaLicencia;
